import React, { useState, useEffect } from 'react';
import authenticationService from '../../services/authenticationService';

const SsoConfiguration = ({ scopeType, scopeId }) => {
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingConfig, setEditingConfig] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    loadConfigurations();
  }, [scopeType, scopeId]);

  const loadConfigurations = async () => {
    try {
      setLoading(true);
      const data = await authenticationService.getSsoConfigurations(scopeType, scopeId);
      setConfigs(data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleTest = async (configId) => {
    try {
      const result = await authenticationService.testSsoConfiguration(configId);
      if (result.success) {
        setSuccess('SSO configuration test passed');
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError('SSO configuration test failed');
      }
      loadConfigurations();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDelete = async (configId) => {
    if (!window.confirm('Are you sure you want to delete this SSO configuration?')) {
      return;
    }

    try {
      await authenticationService.deleteSsoConfiguration(configId);
      setSuccess('SSO configuration deleted successfully');
      setTimeout(() => setSuccess(null), 3000);
      loadConfigurations();
    } catch (err) {
      setError(err.message);
    }
  };

  if (loading) {
    return <div style={{ textAlign: 'center', padding: '40px' }}>Loading...</div>;
  }

  return (
    <div>
      {/* Error/Success messages */}
      {error && (
        <div style={{
          padding: '12px 16px',
          backgroundColor: '#fef2f2',
          border: '1px solid #fecaca',
          borderRadius: '8px',
          color: '#991b1b',
          marginBottom: '16px'
        }}>
          {error}
        </div>
      )}

      {success && (
        <div style={{
          padding: '12px 16px',
          backgroundColor: '#d1fae5',
          border: '1px solid #6ee7b7',
          borderRadius: '8px',
          color: '#065f46',
          marginBottom: '16px'
        }}>
          {success}
        </div>
      )}

      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '24px'
      }}>
        <div>
          <h3 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '4px' }}>
            Single Sign-On (SSO) Configurations
          </h3>
          <p style={{ fontSize: '14px', color: '#6b7280' }}>
            Configure SSO providers to allow users to sign in with their organization credentials
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          style={{
            padding: '10px 24px',
            backgroundColor: '#3b82f6',
            color: '#ffffff',
            border: 'none',
            borderRadius: '6px',
            fontSize: '14px',
            fontWeight: 500,
            cursor: 'pointer'
          }}
        >
          + Add SSO Configuration
        </button>
      </div>

      {/* Configurations List */}
      {configs.length === 0 ? (
        <div style={{
          padding: '40px',
          textAlign: 'center',
          backgroundColor: '#f9fafb',
          border: '1px solid #e5e7eb',
          borderRadius: '8px',
          color: '#6b7280'
        }}>
          <p style={{ fontSize: '14px', marginBottom: '16px' }}>No SSO configurations</p>
          <button
            onClick={() => setShowCreateModal(true)}
            style={{
              padding: '8px 16px',
              backgroundColor: '#3b82f6',
              color: '#ffffff',
              border: 'none',
              borderRadius: '6px',
              fontSize: '14px',
              cursor: 'pointer'
            }}
          >
            Create First Configuration
          </button>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: '16px' }}>
          {configs.map(config => (
            <div
              key={config.id}
              style={{
                padding: '20px',
                backgroundColor: '#ffffff',
                border: '1px solid #e5e7eb',
                borderRadius: '8px'
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '16px' }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                    <h4 style={{ fontSize: '16px', fontWeight: 600 }}>{config.name}</h4>
                    <span style={{
                      padding: '2px 8px',
                      borderRadius: '4px',
                      fontSize: '12px',
                      fontWeight: 500,
                      backgroundColor: config.isActive ? '#d1fae5' : '#f3f4f6',
                      color: config.isActive ? '#065f46' : '#6b7280'
                    }}>
                      {config.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <p style={{ fontSize: '14px', color: '#6b7280' }}>
                    Provider: {config.providerType}
                  </p>
                  {config.lastTestedAt && (
                    <p style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px' }}>
                      Last tested: {new Date(config.lastTestedAt).toLocaleString()}
                    </p>
                  )}
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    onClick={() => handleTest(config.id)}
                    style={{
                      padding: '6px 12px',
                      backgroundColor: '#f3f4f6',
                      color: '#374151',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '12px',
                      fontWeight: 500,
                      cursor: 'pointer'
                    }}
                  >
                    Test
                  </button>
                  <button
                    onClick={() => setEditingConfig(config)}
                    style={{
                      padding: '6px 12px',
                      backgroundColor: '#3b82f6',
                      color: '#ffffff',
                      border: 'none',
                      borderRadius: '6px',
                      fontSize: '12px',
                      fontWeight: 500,
                      cursor: 'pointer'
                    }}
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(config.id)}
                    style={{
                      padding: '6px 12px',
                      backgroundColor: '#fef2f2',
                      color: '#991b1b',
                      border: '1px solid #fecaca',
                      borderRadius: '6px',
                      fontSize: '12px',
                      fontWeight: 500,
                      cursor: 'pointer'
                    }}
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      {(showCreateModal || editingConfig) && (
        <SsoConfigModal
          scopeType={scopeType}
          scopeId={scopeId}
          config={editingConfig}
          onClose={() => {
            setShowCreateModal(false);
            setEditingConfig(null);
          }}
          onSuccess={() => {
            setShowCreateModal(false);
            setEditingConfig(null);
            loadConfigurations();
          }}
        />
      )}
    </div>
  );
};

const SsoConfigModal = ({ scopeType, scopeId, config, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    providerType: config?.providerType || 'SAML',
    name: config?.name || '',
    isActive: config?.isActive || false,
    entityId: config?.entityId || '',
    acsUrl: config?.acsUrl || '',
    metadataXml: config?.metadataXml || '',
    clientId: config?.clientId || '',
    clientSecret: config?.clientSecret || '',
    redirectUris: config?.redirectUris || '',
    attributeMapping: config?.attributeMapping || '',
    scimEnabled: config?.scimEnabled || false,
    groupSyncEnabled: config?.groupSyncEnabled || false
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      setError(null);

      if (config) {
        await authenticationService.updateSsoConfiguration(config.id, formData);
      } else {
        await authenticationService.createSsoConfiguration(scopeType, scopeId, formData);
      }

      onSuccess();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: '#ffffff',
        borderRadius: '8px',
        padding: '24px',
        width: '90%',
        maxWidth: '600px',
        maxHeight: '90vh',
        overflow: 'auto'
      }}>
        <h3 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '20px' }}>
          {config ? 'Edit SSO Configuration' : 'Create SSO Configuration'}
        </h3>

        {error && (
          <div style={{
            padding: '12px 16px',
            backgroundColor: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: '8px',
            color: '#991b1b',
            marginBottom: '16px'
          }}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div style={{ display: 'grid', gap: '16px' }}>
            <div>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, marginBottom: '8px' }}>
                Provider Type
              </label>
              <select
                value={formData.providerType}
                onChange={(e) => setFormData({ ...formData, providerType: e.target.value })}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px'
                }}
              >
                <option value="SAML">SAML 2.0</option>
                <option value="OAuth">OAuth 2.0 / OpenID Connect</option>
                <option value="AzureAD">Azure AD</option>
                <option value="Okta">Okta</option>
                <option value="GoogleWorkspace">Google Workspace</option>
              </select>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, marginBottom: '8px' }}>
                Configuration Name *
              </label>
              <input
                type="text"
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px'
                }}
              />
            </div>

            <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={formData.isActive}
                onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                style={{ width: '18px', height: '18px', marginRight: '8px' }}
              />
              <span style={{ fontSize: '14px' }}>Active</span>
            </label>

            {/* SAML-specific fields */}
            {(formData.providerType === 'SAML') && (
              <>
                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, marginBottom: '8px' }}>
                    Entity ID
                  </label>
                  <input
                    type="text"
                    value={formData.entityId}
                    onChange={(e) => setFormData({ ...formData, entityId: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, marginBottom: '8px' }}>
                    ACS URL
                  </label>
                  <input
                    type="text"
                    value={formData.acsUrl}
                    onChange={(e) => setFormData({ ...formData, acsUrl: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, marginBottom: '8px' }}>
                    Metadata XML
                  </label>
                  <textarea
                    value={formData.metadataXml}
                    onChange={(e) => setFormData({ ...formData, metadataXml: e.target.value })}
                    rows={6}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px',
                      fontFamily: 'monospace'
                    }}
                  />
                </div>
              </>
            )}

            {/* OAuth-specific fields */}
            {(formData.providerType === 'OAuth' || formData.providerType === 'AzureAD' || 
              formData.providerType === 'Okta' || formData.providerType === 'GoogleWorkspace') && (
              <>
                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, marginBottom: '8px' }}>
                    Client ID *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.clientId}
                    onChange={(e) => setFormData({ ...formData, clientId: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, marginBottom: '8px' }}>
                    Client Secret *
                  </label>
                  <input
                    type="password"
                    required={!config}
                    value={formData.clientSecret}
                    onChange={(e) => setFormData({ ...formData, clientSecret: e.target.value })}
                    placeholder={config ? 'Leave blank to keep existing' : ''}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, marginBottom: '8px' }}>
                    Redirect URIs (JSON array)
                  </label>
                  <textarea
                    value={formData.redirectUris}
                    onChange={(e) => setFormData({ ...formData, redirectUris: e.target.value })}
                    placeholder='["https://example.com/callback"]'
                    rows={3}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px',
                      fontFamily: 'monospace'
                    }}
                  />
                </div>
              </>
            )}

            <div>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, marginBottom: '8px' }}>
                Attribute Mapping (JSON)
              </label>
              <textarea
                value={formData.attributeMapping}
                onChange={(e) => setFormData({ ...formData, attributeMapping: e.target.value })}
                placeholder='{"email": "email", "name": "displayName"}'
                rows={4}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontFamily: 'monospace'
                }}
              />
            </div>

            <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={formData.scimEnabled}
                onChange={(e) => setFormData({ ...formData, scimEnabled: e.target.checked })}
                style={{ width: '18px', height: '18px', marginRight: '8px' }}
              />
              <span style={{ fontSize: '14px' }}>Enable SCIM (automatic user provisioning)</span>
            </label>

            <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={formData.groupSyncEnabled}
                onChange={(e) => setFormData({ ...formData, groupSyncEnabled: e.target.checked })}
                style={{ width: '18px', height: '18px', marginRight: '8px' }}
              />
              <span style={{ fontSize: '14px' }}>Enable group/role synchronization</span>
            </label>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '24px' }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                padding: '10px 24px',
                backgroundColor: '#f3f4f6',
                color: '#374151',
                border: 'none',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: 500,
                cursor: 'pointer'
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              style={{
                padding: '10px 24px',
                backgroundColor: '#3b82f6',
                color: '#ffffff',
                border: 'none',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: 500,
                cursor: saving ? 'not-allowed' : 'pointer',
                opacity: saving ? 0.6 : 1
              }}
            >
              {saving ? 'Saving...' : config ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SsoConfiguration;

